(function () {
  'use strict';

  if (window.metaboxMwCameraMapping) return;

  const normalizeHelpers = window.metaboxMwNormalize || {};
  const CAMERA_ANIMATION_DURATION = 1000;
  const MOBILE_FULLSCREEN_BREAKPOINT = '(max-width: 999px)';

  const asString = normalizeHelpers.asString || ((value) => String(value || '').trim());
  const normalizeOptionName =
    normalizeHelpers.normalizeOptionName || ((value) => String(value || '').trim().toLowerCase());
  const normalizeEntityName = normalizeHelpers.normalizeEntityName || normalizeOptionName;
  const getMwValueLabel = normalizeHelpers.getMwValueLabel || (() => '');

  function parseCameraConfig() {
    const configElement = document.querySelector('[data-metabox-mw-camera-config]');
    if (!configElement) return {};

    try {
      return JSON.parse(configElement.textContent || '{}');
    } catch (error) {
      console.error('[Metabox-MW-Camera] Failed to parse camera mapping config', error);
      return {};
    }
  }

  const metaboxMwCameraConfig = parseCameraConfig();
  const CAMERA_MAPPING_ROWS = Array.isArray(metaboxMwCameraConfig.cameraMappingRows)
    ? metaboxMwCameraConfig.cameraMappingRows
    : [];

  let cameraActionQueue = Promise.resolve();

  const cameraRules = parseCameraMappingRows(CAMERA_MAPPING_ROWS);
  const cameraRulesByOption = buildCameraRulesByOption(cameraRules);

  function parseCameraMappingRows(rows) {
    if (!Array.isArray(rows) || rows.length === 0) return [];

    return rows
      .map((row) => {
        const optionLabelRaw = asString(row && row.optionLabel);
        const optionValueRaw = asString(row && row.optionValue);
        const whenSelected = normalizeWhenSelected(row && row.whenSelected);
        const cameraPayload = normalizeCameraPayload(row && row.cameraPayload);
        const cameraPayloadFullscreen = normalizeCameraPayload(row && row.cameraPayloadFullscreen);

        if (
          !optionLabelRaw ||
          !optionValueRaw ||
          (!cameraPayload && !cameraPayloadFullscreen)
        ) {
          return null;
        }

        return {
          optionLabelRaw,
          optionLabelNorm: normalizeOptionName(optionLabelRaw),
          optionValueRaw,
          optionValueNorm: normalizeEntityName(optionValueRaw),
          whenSelected,
          cameraPayload,
          cameraPayloadFullscreen,
        };
      })
      .filter(Boolean);
  }

  function normalizeWhenSelected(value) {
    if (typeof value === 'boolean') return value;

    const textValue = asString(value).toLowerCase();
    if (['true', '1', 'yes'].includes(textValue)) return true;
    if (['false', '0', 'no'].includes(textValue)) return false;

    return true;
  }

  function normalizeCameraPayload(value) {
    if (!value) return null;

    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch (error) {
        return null;
      }
    }

    return typeof value === 'object' ? value : null;
  }

  function buildCameraRulesByOption(rules) {
    const map = new Map();

    rules.forEach((rule) => {
      if (!rule.optionLabelNorm) return;

      if (!map.has(rule.optionLabelNorm)) {
        map.set(rule.optionLabelNorm, []);
      }

      map.get(rule.optionLabelNorm).push(rule);
    });

    return map;
  }

  function getRulesForOption(optionLabel) {
    const optionLabelNorm = normalizeOptionName(optionLabel);
    if (!optionLabelNorm || !cameraRulesByOption.has(optionLabelNorm)) return [];
    return cameraRulesByOption.get(optionLabelNorm);
  }

  function shouldRunRuleOnInputState(rule, isChecked) {
    if (!rule) return false;
    return rule.whenSelected ? isChecked : !isChecked;
  }

  function connectCameraMappings() {
    if (document.__metaboxMwCameraPointerDownHandler) {
      return true;
    }

    const resolveControlFromEventTarget = (target) => {
      if (!(target instanceof Element)) return null;

      const directControl = target.closest('.mw-option__control');
      if (directControl) return directControl;

      const valueScope = target.closest('.mw-option__value');
      if (valueScope) {
        return valueScope.querySelector('.mw-option__control');
      }

      const optionScope = target.closest('.mw-option');
      if (optionScope) {
        return optionScope.querySelector('select.mw-option__control');
      }

      return null;
    };

    const pointerDownHandler = (event) => {
      if (!event.target?.closest('.mw-apo-container')) return;
      const control = resolveControlFromEventTarget(event.target);
      if (!(control instanceof HTMLInputElement)) return;
      control.__metaboxMwCameraCheckedBeforePointerDown = Boolean(control.checked);
    };

    const clickHandler = (event) => {
      if (!event.target?.closest('.mw-apo-container')) return;
      const control = resolveControlFromEventTarget(event.target);
      if (!(control instanceof HTMLInputElement) || control.type !== 'radio') return;

      const wasCheckedBeforePointerDown = Boolean(control.__metaboxMwCameraCheckedBeforePointerDown);
      if (wasCheckedBeforePointerDown) {
        applyCameraRulesForControl(control, false, 'unchecked', 'click-reclick-selected-radio');
      }

      control.__metaboxMwCameraCheckedBeforePointerDown = undefined;
    };

    const changeHandler = (event) => {
      if (!event.target?.closest('.mw-apo-container')) return;
      const control = resolveControlFromEventTarget(event.target);
      if (!control) return;

      const hasCheckedState = control.type === 'radio' || control.type === 'checkbox';
      const isChecked = hasCheckedState ? Boolean(control.checked) : true;
      const triggerState = isChecked ? 'checked' : 'unchecked';

      applyCameraRulesForControl(control, isChecked, triggerState, 'change');
    };

    document.__metaboxMwCameraPointerDownHandler = pointerDownHandler;
    document.__metaboxMwCameraClickHandler = clickHandler;
    document.__metaboxMwCameraChangeHandler = changeHandler;

    document.addEventListener('pointerdown', pointerDownHandler, true);
    document.addEventListener('click', clickHandler);
    document.addEventListener('change', changeHandler);

    return true;
  }

  function applyCameraRulesForControl(control, isChecked, triggerState, source) {
    const mwOption = control.closest('.mw-option');
    if (!mwOption) return;

    const mwOptionName = getMwOptionLabel(mwOption);
    const rulesForOption = getRulesForOption(mwOptionName);
    if (!rulesForOption.length) return;

    const optionValueLabel = getControlValueLabel(control, mwOption);
    const optionValueNorm = normalizeEntityName(optionValueLabel);
    if (!optionValueNorm) return;

    const applicableRules = rulesForOption.filter((rule) => {
      return rule.optionValueNorm === optionValueNorm && shouldRunRuleOnInputState(rule, isChecked);
    });

    if (!applicableRules.length) return;

    applicableRules.forEach((rule, ruleIndex) => {
      queueCameraRuleExecution(rule, {
        mwOptionName,
        optionValueLabel,
        triggerState,
        source,
        ruleIndex,
        rulesCount: applicableRules.length,
      });
    });
  }

  function getMwOptionLabel(mwOption) {
    return mwOption.querySelector('.mw-text.mw-text--style-strong')?.textContent?.trim() || '';
  }

  function getControlValueLabel(control, mwOption) {
    if (!control) return '';

    if (control.tagName === 'SELECT') {
      const selectedOption = control.options && control.selectedIndex >= 0 ? control.options[control.selectedIndex] : null;
      return selectedOption?.textContent?.trim() || asString(control.value);
    }

    const mwValue = control.closest('.mw-option__value');
    if (!mwValue) return asString(control.value);

    const mwValues = Array.from(mwOption.querySelectorAll('.mw-option__value'));
    const mwValueIndex = mwValues.indexOf(mwValue);
    return getMwValueLabel(mwValue, mwValueIndex >= 0 ? mwValueIndex : 0);
  }

  function queueCameraRuleExecution(rule, context) {
    cameraActionQueue = cameraActionQueue
      .then(() => executeCameraRule(rule, context))
      .catch(() => false);

    return cameraActionQueue;
  }

  function isFullscreenMode() {
    return document.documentElement.classList.contains('mw-configure-fullscreen-active');
  }

  function isMobileFullscreenMode() {
    return isFullscreenMode() && window.matchMedia(MOBILE_FULLSCREEN_BREAKPOINT).matches;
  }

  function resolveCameraPayloadForRule(rule) {
    if (!rule) return null;

    if (isFullscreenMode() && !isMobileFullscreenMode() && rule.cameraPayloadFullscreen) {
      return rule.cameraPayloadFullscreen;
    }

    return rule.cameraPayload || rule.cameraPayloadFullscreen || null;
  }

  function executeCameraRule(rule) {
    if (!(window.metaboxConfigurator && typeof window.metaboxConfigurator.setCamera === 'function')) {
      return false;
    }

    const targetCameraPayload = resolveCameraPayloadForRule(rule);
    if (!targetCameraPayload) return false;

    if (typeof window.metaboxConfigurator.animateCameraTo === 'function') {
      return window.metaboxConfigurator.animateCameraTo(targetCameraPayload, {
        duration: CAMERA_ANIMATION_DURATION,
      });
    }

    return window.metaboxConfigurator.setCamera(targetCameraPayload);
  }

  function reapplyCheckedSelections() {
    const mwApoContainer = document.querySelector('.mw-apo-container');
    if (!mwApoContainer) return Promise.resolve(false);

    const controls = Array.from(mwApoContainer.querySelectorAll('.mw-option__control'));
    controls.forEach((control) => {
      if (!control) return;

      if (control.tagName === 'SELECT') {
        if (asString(control.value)) {
          control.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }

      if ((control.type === 'radio' || control.type === 'checkbox') && !control.checked) return;
      control.dispatchEvent(new Event('change', { bubbles: true }));
    });

    return cameraActionQueue;
  }

  if (cameraRules.length) {
    connectCameraMappings();
  }

  window.metaboxMwCameraMapping = {
    reapplyCheckedSelections,
    getMappingRules: () => cameraRules.slice(),
    getRulesForOption: (optionLabel) => getRulesForOption(optionLabel).slice(),
  };
})();
